// Public read of the Tile Editor's per-site overrides from Supabase.
//
// Replaces the Apps Script `?action=tileOverrides&site=<key>` feed the FOH and kitchen
// home screens used — same PUBLIC exposure (the old feed was an unauthenticated GET),
// just served from Supabase now. Writes stay admin-only (Command Centre → admin-data).
//
// ─── THE MERGE IS THE WHOLE JOB ──────────────────────────────────────────────
// The old feed did the merge SERVER-side and handed the tablet a finished list. Reading
// Supabase directly gives us the RAW rows, so the merge moves here — and it has to match
// the old behaviour exactly, or tiles silently change on four live kiosks.
//
// The rule, lifted from handleGetTileOverrides in ManchesterForecastEngine.js:
//
//     pass 1: every row with site_key '*'   -> byTile[tile_id] = row
//     pass 2: every row for THIS site       -> byTile[tile_id] = row   (replaces it)
//
// It is a WHOLE-ROW REPLACE, not a per-field merge. A site row with a blank label does
// NOT inherit the global label — it replaces the global row outright, and the blank then
// falls through to the app's own hardcoded default in applyTileOverride().
//
// This is observable on the live estate today: the global `stock` row sets desc
// "Request items needed at kiosk". Meadowhall has no per-site stock row, so it shows
// that text. Trafford and Arndale DO have one (blank desc), so they replace the global
// row and show the app's built-in "Request items needed at site" instead. Faithfully
// reproduced here — a migration must not change what staff see. (Worth fixing one day,
// but as its own decision, not smuggled in with a database move. NB the Command Centre's
// own preview does a per-field merge, so it disagrees with the tablets on exactly this.)
//
// Returns the same map the tablets already build from the old feed — keyed by tileId,
// values in the API's shape (`desc`, not the column's `description`) — so
// applyTileOverride() is untouched.
import { supabase } from "./supabaseClient.js";

// The kitchen tablet's SITE.key is 'mcr', but the Tile Editor writes the kitchen under
// 'kitchen'. The old backend normalised this server-side (normalizeSiteKey_); miss it and
// the kitchen silently loses all 22 of its tile settings. Mirrors migrate_tile_overrides.mjs.
export function normaliseSiteKey(raw) {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "tc" || s === "trafford" || s === "trafford centre") return "tc";
  if (s === "arndale" || s === "arn") return "arndale";
  if (s === "meadowhall") return "meadowhall";
  if (s === "kitchen" || s === "mcr" || s === "manchester kitchen") return "kitchen";
  return s;
}

// Exported so it can be tested without a network — see tileOverrides.test.mjs, which pins
// every rule below (whole-row replace, the mcr->kitchen translation, admin-home isolation,
// and the exact field shape applyTileOverride reads). `rows` are raw table rows in; returns
// the tileId-keyed map. Per-site parity against the LIVE feed: supabase/_tiles_parity_check.mjs.
export function mergeOverrides(rows, siteKey) {
  const site = normaliseSiteKey(siteKey);
  const byTile = {};
  const put = (r) => {
    byTile[r.tile_id] = {
      siteKey: r.site_key,
      tileId: r.tile_id,
      label: r.label || "",
      desc: r.description || "",        // column `description` -> API field `desc`
      icon: r.icon || "",
      state: r.state,
      comingSoon: r.coming_soon === true,
      iconKind: r.icon_kind || "emoji",
      thumbnailUrl: r.thumbnail_url || "",
      bannerUrl: r.banner_url || "",
      manages: r.manages || "",
    };
  };
  // Pass 1 — the global default.
  for (const r of rows) if (r.site_key === "*") put(r);
  // Pass 2 — this site REPLACES the whole row. Match the normalised key OR the raw one,
  // exactly as the old backend did, so a legacy row under a synonym still applies.
  for (const r of rows) if (r.site_key === site || r.site_key === String(siteKey ?? "").trim().toLowerCase()) put(r);
  return byTile;
}

export async function fetchTileOverrides(siteKey) {
  const site = normaliseSiteKey(siteKey);
  const { data, error } = await supabase
    .from("tile_overrides")
    .select("site_key,tile_id,label,description,icon,state,coming_soon,icon_kind,thumbnail_url,banner_url,manages")
    .or(`site_key.eq.*,site_key.eq.${site}`);
  if (error) throw new Error(error.message || "Failed to load tile overrides");
  // ── An empty read is a FAILURE here, not a valid "no overrides" answer ──────
  // This is the one way the new backend can fail that the old feed could not. RLS does
  // not error on denial — it returns 200 with []. And no RLS fault is even needed: the
  // schema and the row copy are separate steps, so "migration applied, copy not yet run"
  // is an ordinary deploy state that also returns zero rows.
  //
  // Without this throw, mergeOverrides([]) returns {}, which is a perfectly good object —
  // so the caller setState's it AND writes it to localStorage. The kiosk then reverts every
  // tile to its hardcoded default (any tile the CC set to coming_soon/hidden but which is
  // `on:true` in code REAPPEARS), and because the poisoned {} is now cached, reloading the
  // tablet does not heal it — it needs a per-device storage wipe. On all four kiosks at once.
  //
  // The old Apps Script feed could not do this: `r.ok ? r.json() : null` turned any backend
  // failure into d=null, which the caller ignored and kept its cache. Throwing here routes
  // into the caller's existing .catch(() => {/* keep cache */}) and restores that behaviour.
  //
  // A genuinely empty table is not a real state: the estate always has the '*' rows, and the
  // copy tool likewise refuses to run on an empty feed. Treating empty as broken is correct.
  if (!data || data.length === 0) {
    throw new Error("tile_overrides returned no rows — refusing to overwrite the cache");
  }
  return mergeOverrides(data, siteKey);
}
